import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Plane, AlertCircle, Eye, EyeOff, ArrowLeft } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Already authenticated → skip login, go straight to dashboard
  useEffect(() => {
    if (user) {
      const path = user.role === "pilot" || user.role === "ame" ? "/crew" : "/dashboard";
      navigate(path, { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const loggedInUser = await login(email.trim().toLowerCase(), password);
      const path = loggedInUser.role === "pilot" || loggedInUser.role === "ame" ? "/crew" : "/dashboard";
      navigate(path, { replace: true });
    } catch {
      setError("Invalid credentials. Please check your email and password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background" data-testid="login-page">
      {/* Left Hero */}
      <div className="hidden lg:flex lg:w-3/5 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/10320757/pexels-photo-10320757.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920"
          alt="Aviation operations"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/80 to-background/20" />
        <div className="relative z-10 flex flex-col justify-end p-12 pb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex items-center justify-center w-10 h-10 bg-primary rounded-sm">
              <Plane size={20} className="text-white" />
            </div>
          </div>
          <h1 className="text-5xl font-black tracking-tight text-foreground font-heading leading-tight mb-3">
            FATIGUE<br /><span className="text-primary">SHIELD</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-sm leading-relaxed mb-8">
            Predict fatigue. Protect safety.<br />Sovereign-built for DGCA-regulated airlines.
          </p>
          <div className="grid grid-cols-3 gap-4">
            {[["7", "Modules"], ["20+", "Risk Metrics"], ["Real-time", "AI Analysis"]].map(([val, lbl]) => (
              <div key={lbl} className="border border-border p-3 rounded-sm">
                <div className="text-2xl font-mono font-medium text-primary">{val}</div>
                <div className="text-xs text-muted-foreground tracking-widest uppercase mt-1">{lbl}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Form */}
      <div className="flex-1 flex flex-col justify-center px-8 py-12 lg:max-w-md">
        <div className="w-full max-w-sm mx-auto">
          {/* Home button */}
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors mb-8"
            data-testid="back-to-home-link"
          >
            <ArrowLeft size={13} />
            Back to Home
          </Link>
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-sm">
              <Plane size={16} className="text-white" />
            </div>
            <span className="text-xl font-black tracking-tight font-heading text-foreground">FATIGUE SHIELD</span>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold font-heading text-foreground mb-1">Admin Sign-In</h2>
            <p className="text-sm text-muted-foreground">Access your airline fatigue management console</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs tracking-widest uppercase text-muted-foreground font-bold block mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="admin@airline.com"
                required
                autoComplete="username"
                className="w-full bg-card border border-border rounded-sm px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="email-input"
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-muted-foreground font-bold block mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  autoComplete="current-password"
                  className="w-full bg-card border border-border rounded-sm px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary pr-10"
                  data-testid="password-input"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-destructive text-xs bg-destructive/10 border border-destructive/20 rounded-sm px-3 py-2" data-testid="login-error">
                <AlertCircle size={14} />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-primary-foreground font-semibold py-2.5 rounded-sm text-sm hover:bg-primary/90 transition-colors disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {loading ? "Authenticating..." : "Access Console"}
            </button>
          </form>

          <p className="mt-6 text-xs text-muted-foreground text-center">
            Access is restricted to authorised personnel only.
          </p>
        </div>
      </div>
    </div>
  );
}
